/**
 * Client-side spatial control — a 1:1 port of the KNN pitch-control model in
 * app/analytics/spatial_control_parquet.py (_extract_player_teams +
 * _compute_frame_control). The browser already holds every player position
 * (trackingLoader), and one frame computes in ~0.2 ms, so the viewer computes
 * grids on demand instead of downloading precomputed 24 MB windows.
 *
 * PARITY IS THE CONTRACT. Every constant and step below mirrors the Python
 * pipeline, which remains the source of truth for the server-side analytics
 * that consume stored grids (attacking_effectiveness, pass_potential, …).
 * A change to the model happens in Python first; this port follows. The
 * layout of the returned grid matches the pipeline's flattened float32 blob:
 * index = gridX * GRID_Y_CELLS + gridY (row-major over a (106, 69) array).
 */

export const GRID_X_CELLS = 106 // ~1m per cell along the pitch length
export const GRID_Y_CELLS = 69 // ~1m per cell along the pitch width
const PITCH_LENGTH = 10500 // centimeters, matching tracking data
const PITCH_WIDTH = 6800
const EPSILON = 350 // distance weighting factor
const MIN_TEAM_PLAYERS = 4 // matches SpatialControlParquetAnalytics.MIN_TEAM_PLAYERS
const GRID_SIZE = GRID_X_CELLS * GRID_Y_CELLS

export interface SpatialPlayerLike {
  team: number
  x: number
  y: number
}

export interface ComputedSpatialFrame {
  grid: Float32Array
  team0Pct: number
  team1Pct: number
}

// Grid coordinates, precomputed once. numpy linspace: inclusive endpoints.
// Kept as Float64Array — the Python pipeline computes in float64 and casts
// to float32 only at the end; matching that keeps the parity diff at
// float32-rounding level.
const gridX = new Float64Array(GRID_SIZE)
const gridY = new Float64Array(GRID_SIZE)
for (let xi = 0; xi < GRID_X_CELLS; xi++) {
  const x = -PITCH_LENGTH / 2 + (PITCH_LENGTH * xi) / (GRID_X_CELLS - 1)
  for (let yi = 0; yi < GRID_Y_CELLS; yi++) {
    const i = xi * GRID_Y_CELLS + yi
    gridX[i] = x
    gridY[i] = -PITCH_WIDTH / 2 + (PITCH_WIDTH * yi) / (GRID_Y_CELLS - 1)
  }
}

// Scratch buffers reused across calls (single-threaded, no reentrancy).
const rawControl = new Float64Array(GRID_SIZE)
const smoothed = new Float64Array(GRID_SIZE)

/**
 * Compute one frame's control grid from its players.
 *
 * Mirrors the pipeline: detect the two most-populated team ids (excluding
 * referees), map the LOWER id to team A (+1 control) and the higher to team
 * B (−1), weight each cell by the nearest player's distance, 3×3 box-smooth
 * with reflected edges (scipy uniform_filter's default mode), normalize to
 * [−1, 1]. Returns null when fewer than two teams are present — the same
 * frames the pipeline skips, so the overlay shows the same gaps.
 */
export function computeControlGrid(players: ReadonlyArray<SpatialPlayerLike>): ComputedSpatialFrame | null {
  if (!players || players.length === 0) return null

  // -- _extract_player_teams: two most common team ids, lower id = team A.
  // Negative ids are the "unidentified" sentinel, never a team; and both
  // teams must field at least MIN_TEAM_PLAYERS identified players, or the
  // frame is skipped (degraded identity tracking — e.g. SKC/15 frames
  // ~87k-110k label 27 of 29 bodies -1, and "control" computed from the
  // survivors is noise). Mirrors the pipeline's guard exactly. --
  const counts = new Map<number, number>()
  for (const p of players) {
    if (p.team < 0) continue
    counts.set(p.team, (counts.get(p.team) ?? 0) + 1)
  }
  if (counts.size < 2) return null
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2)
  if (ranked[1]![1] < MIN_TEAM_PLAYERS) return null
  const topTwo = ranked.map((e) => e[0]).sort((a, b) => a - b)
  const teamAId = topTwo[0]
  const teamBId = topTwo[1]
  if (teamAId === undefined || teamBId === undefined) return null

  const px: number[] = []
  const py: number[] = []
  const psign: number[] = [] // +1 for team A (normalized 0), −1 for team B
  for (const p of players) {
    if (p.team === teamAId) {
      px.push(p.x)
      py.push(p.y)
      psign.push(1)
    } else if (p.team === teamBId) {
      px.push(p.x)
      py.push(p.y)
      psign.push(-1)
    }
    // Other ids (referees, unidentified) are excluded, as in the pipeline.
  }
  const n = px.length
  if (n < 2) return null // pipeline: `if len(positions) < 2: continue`

  // -- _compute_frame_control: nearest player + epsilon distance weighting --
  for (let c = 0; c < GRID_SIZE; c++) {
    const cx = gridX[c]!
    const cy = gridY[c]!
    let bestD2 = Infinity
    let sign = 0
    for (let p = 0; p < n; p++) {
      const dx = cx - px[p]!
      const dy = cy - py[p]!
      const d2 = dx * dx + dy * dy
      if (d2 < bestD2) {
        bestD2 = d2
        sign = psign[p]!
      }
    }
    rawControl[c] = Math.pow(EPSILON, 1 - Math.sqrt(bestD2) / 4000) * sign
  }

  // -- 3×3 uniform filter, 'reflect' boundaries (scipy default) --
  let maxAbs = 0
  for (let xi = 0; xi < GRID_X_CELLS; xi++) {
    for (let yi = 0; yi < GRID_Y_CELLS; yi++) {
      let sum = 0
      for (let dx = -1; dx <= 1; dx++) {
        let x = xi + dx
        if (x < 0) x = -x - 1
        else if (x >= GRID_X_CELLS) x = 2 * GRID_X_CELLS - x - 1
        for (let dy = -1; dy <= 1; dy++) {
          let y = yi + dy
          if (y < 0) y = -y - 1
          else if (y >= GRID_Y_CELLS) y = 2 * GRID_Y_CELLS - y - 1
          sum += rawControl[x * GRID_Y_CELLS + y]!
        }
      }
      const v = sum / 9
      smoothed[xi * GRID_Y_CELLS + yi] = v
      const a = Math.abs(v)
      if (a > maxAbs) maxAbs = a
    }
  }

  // -- normalize to [−1, 1], cast to float32 like the pipeline's astype --
  const grid = new Float32Array(GRID_SIZE)
  let pos = 0
  let neg = 0
  const inv = maxAbs > 0 ? 1 / maxAbs : 0
  for (let c = 0; c < GRID_SIZE; c++) {
    const v = smoothed[c]! * inv
    grid[c] = v
    if (v > 0) pos++
    else if (v < 0) neg++
  }

  return {
    grid,
    team0Pct: Math.round((pos / GRID_SIZE) * 1000) / 10,
    team1Pct: Math.round((neg / GRID_SIZE) * 1000) / 10,
  }
}
